import { useCallback } from "react";
import type { Room } from "livekit-client";
import { logger } from "@/utils/logger";
import { useCompatibleVoiceAssistant } from "@/hooks/use-compatible-voice-assistant";

// ! Remember python uses snake_case for RPC methods
type RpcMap = {
  user_context: { payload: { id: number | undefined }; response: void };
  switch_mode: { payload: { audio_enable: boolean }; response: string };
  catalog_snapshot: {
    payload: {
      products: unknown[];
      reset: boolean;
      complete: boolean;
    };
    response: string;
  };
  // Add more methods and their types here
} & Record<string, { payload: any; response: any }>;

export type RpcRequest<P extends keyof RpcMap> = {
  method: P;
  payload: RpcMap[P]["payload"];
};

/**
 * Hook to send RPC calls through LiveKit.
 * @param room LiveKit Room instance
 * @returns A typed function to perform RPC calls
 */
export function useLivekitRpc(room: Room) {
  /**
   * Sends an RPC call via LiveKit.
   * @param method Name of the RPC method
   * @param payload Payload for the RPC call
   * @returns The RPC response
   */
  const voiceAssistant = useCompatibleVoiceAssistant();

  return useCallback(
    async <M extends keyof RpcMap>(
      method: M,
      payload: RpcMap[M]["payload"],
    ): Promise<RpcMap[M]["response"]> => {
      if (!voiceAssistant.agent || !room) {
        throw new Error("No agent or room available");
      }

      logger.log("RPC Call:", method, payload);
      const response = await room.localParticipant.performRpc({
        method: method,
        payload: JSON.stringify(payload),
        destinationIdentity: voiceAssistant.agent.identity,
      });

      logger.log("RPC Response:", response);
      return response as RpcMap[M]["response"];
    },
    [room, voiceAssistant.agent],
  );
}
